import { type UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getGetConvertResultSettlementStatisticsExpenditureNaturesQueryKey,
  getGetConvertResultSettlementStatisticsExpenditurePurposesQueryKey,
  getGetConvertResultSettlementStatisticsRevenuePurposesQueryKey,
  usePatchSettlementStatisticsExpenditureNature,
  usePatchSettlementStatisticsExpenditurePurpose,
  usePatchSettlementStatisticsRevenuePurpose,
} from '@/entities/settlementstatisticsconverter/api/generated/api';
import type { ErrorResponse } from '@/entities/settlementstatisticsconverter/api/generated/model/errorResponse';
import type { SuccessResponse } from '@/entities/settlementstatisticsconverter/api/generated/model/successResponse';
import type { MasterType } from '@/entities/settlementstatisticsconverter/lib/master-type';
import type { ErrorType } from '@/shared/api/custom-instance';
import { toast } from '@/shared/ui';

/**
 * マスタ種別に応じた PATCH mutation を返すフック
 * useMasterCreateMutation と同パターンで masterType で切り替える
 */
export const useMasterEditMutation = ({
  resultId,
  masterType,
  masterId,
  onSuccess,
}: {
  resultId: string;
  masterType: MasterType;
  masterId: string;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const masterTitle = t('settlement-statistics-converter.navigation.title');

  const queryKeyMap = {
    expenditure_purpose: getGetConvertResultSettlementStatisticsExpenditurePurposesQueryKey,
    expenditure_nature: getGetConvertResultSettlementStatisticsExpenditureNaturesQueryKey,
    revenue_purpose: getGetConvertResultSettlementStatisticsRevenuePurposesQueryKey,
  } as const;

  const mutationOptions: {
    mutation: Pick<
      UseMutationOptions<SuccessResponse, ErrorType<ErrorResponse>, unknown>,
      'onSuccess' | 'onError'
    >;
  } = {
    mutation: {
      onSuccess: () => {
        toast.success(t('common.messages.update-success', { name: masterTitle }));
        queryClient.invalidateQueries({
          queryKey: queryKeyMap[masterType](resultId),
        });
        onSuccess();
      },
      onError: () => {
        toast.error(t('common.messages.update-failed', { name: masterTitle }));
      },
    },
  };

  const expenditurePurposeMutation =
    usePatchSettlementStatisticsExpenditurePurpose(mutationOptions);
  const expenditureNatureMutation = usePatchSettlementStatisticsExpenditureNature(mutationOptions);
  const revenuePurposeMutation = usePatchSettlementStatisticsRevenuePurpose(mutationOptions);

  const mutate = (leafName: string) => {
    const mutateMap = {
      expenditure_purpose: () =>
        expenditurePurposeMutation.mutate({
          convertResultId: resultId,
          settlementStatisticsExpenditurePurposeId: masterId,
          data: { leaf_name: leafName },
        }),
      expenditure_nature: () =>
        expenditureNatureMutation.mutate({
          convertResultId: resultId,
          settlementStatisticsExpenditureNatureId: masterId,
          data: { leaf_name: leafName },
        }),
      revenue_purpose: () =>
        revenuePurposeMutation.mutate({
          convertResultId: resultId,
          settlementStatisticsRevenuePurposeId: masterId,
          data: { leaf_name: leafName },
        }),
    } as const;

    mutateMap[masterType]();
  };

  const isPendingMap = {
    expenditure_purpose: expenditurePurposeMutation.isPending,
    expenditure_nature: expenditureNatureMutation.isPending,
    revenue_purpose: revenuePurposeMutation.isPending,
  } as const;

  return { mutate, isPending: isPendingMap[masterType] };
};
